"use client";

import { useEffect, useState } from "react";

/**
 * True while the element an in-page link points at (`#book-walkthrough`) is
 * on screen. Sticky mobile CTA bars use it to step aside once the visitor has
 * reached the form they link to. Off-page hrefs (`/start-project`) and a
 * missing element always return false.
 */
export function useInPageTargetInView(href: string): boolean {
  // Keyed by href, so a stale reading for a previous target never leaks into
  // the new one (no synchronous reset inside the effect).
  const [seen, setSeen] = useState<{ href: string; inView: boolean } | null>(
    null,
  );

  useEffect(() => {
    if (!href.startsWith("#") || href.length < 2) return;
    const target = document.getElementById(href.slice(1));
    if (!target) return;
    const observer = new IntersectionObserver(([entry]) =>
      setSeen({ href, inView: Boolean(entry?.isIntersecting) }),
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [href]);

  return seen?.href === href && seen.inView;
}
